import {
	Anchor,
	Badge,
	Button,
	Group,
	Loader,
	Paper,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
	deleteGuestUser,
	guestUserKeys,
} from "@/features/users/api/admin-users";
import type {
	GuestUserRow,
	ListGuestUsersParams,
} from "@/features/users/api/types";
import { loadGuestUsers } from "@/features/users/lib/load-guest-users";
import { useUsersRefresh } from "@/features/users/lib/use-users-refresh";
import { parseUsersRefreshMs } from "@/features/users/lib/users-refresh";
import { describeApiErrorSync } from "@/shared/api/error-message";
import type { PageLimit } from "@/shared/api/types";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

type UserSort = NonNullable<ListGuestUsersParams["sort"]>;

function nextSort(
	current: UserSort,
	order: "ASC" | "DESC",
	column: UserSort,
): { sort: UserSort; order: "ASC" | "DESC" } {
	if (current !== column) {
		const newestFirst =
			column === "createdAt" || column === "deletionRequested";
		return { sort: column, order: newestFirst ? "DESC" : "ASC" };
	}
	return { sort: column, order: order === "ASC" ? "DESC" : "ASC" };
}

function SortHeader({
	label,
	order,
	active,
	onSort,
}: {
	label: string;
	order?: "ASC" | "DESC";
	active: boolean;
	onSort: () => void;
}) {
	const mark = active ? (order === "ASC" ? " ↑" : " ↓") : "";
	return (
		<Table.Th>
			<UnstyledButton onClick={onSort}>
				{label}
				{mark}
			</UnstyledButton>
		</Table.Th>
	);
}

export function UsersPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<PageLimit>(20);
	const [sort, setSort] = useState<UserSort>("createdAt");
	const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
	const [deletionOnly, setDeletionOnly] = useState(false);
	const { refreshMs, setRefreshMs } = useUsersRefresh();
	const [pendingDelete, setPendingDelete] = useState<GuestUserRow | null>(null);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const params: ListGuestUsersParams = useMemo(
		() => ({
			search:
				debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : undefined,
			page,
			limit,
			sort,
			order,
			deletionRequested: deletionOnly || undefined,
		}),
		[debouncedSearch, page, limit, sort, order, deletionOnly],
	);

	const queryClient = useQueryClient();
	const listQuery = useQuery({
		queryKey: guestUserKeys.list(params),
		queryFn: () => loadGuestUsers(params),
		refetchInterval: refreshMs > 0 ? refreshMs : false,
		refetchIntervalInBackground: false,
	});
	const deleteMutation = useMutation({
		mutationFn: deleteGuestUser,
		onSuccess: async () => {
			setPendingDelete(null);
			setDeleteError(null);
			await queryClient.invalidateQueries({ queryKey: guestUserKeys.all });
		},
		onError: (error) => {
			const message = describeApiErrorSync(error);
			setDeleteError(message);
			notifications.show({
				color: "red",
				title: t("users.deleteFailed"),
				message,
			});
		},
	});

	const updatedAt = listQuery.dataUpdatedAt
		? new Date(listQuery.dataUpdatedAt).toLocaleTimeString("uk-UA", {
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			})
		: null;

	const onSort = (column: UserSort) => {
		const next = nextSort(sort, order, column);
		setSort(next.sort);
		setOrder(next.order);
		setPage(1);
	};

	return (
		<Stack gap="md">
			<Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
				<div>
					<Group gap="xs" align="center">
						<Title order={2}>{t("users.title")}</Title>
						{typeof listQuery.data?.totalItemsCount === "number" ? (
							<Badge size="sm" variant="filled" circle>
								{listQuery.data.totalItemsCount}
							</Badge>
						) : null}
					</Group>
					<Text c="dimmed" size="sm">
						{t("users.subtitle")}
					</Text>
				</div>
				<Select
					label={t("applications.refreshLabel")}
					w={{ base: "100%", sm: 180 }}
					data={[
						{ value: "0", label: t("applications.refreshOff") },
						{ value: "15000", label: t("applications.refresh15s") },
						{ value: "30000", label: t("applications.refresh30s") },
						{ value: "60000", label: t("applications.refresh1m") },
						{ value: "120000", label: t("applications.refresh2m") },
					]}
					value={String(refreshMs)}
					onChange={(value) => {
						if (value != null) {
							setRefreshMs(parseUsersRefreshMs(value));
						}
					}}
					allowDeselect={false}
				/>
			</Group>

			<Paper p="md" radius="lg" withBorder>
				<Stack gap="md">
					<Group grow preventGrowOverflow={false} wrap="wrap">
						<TextInput
							label={t("common.search")}
							placeholder={t("users.searchPlaceholder")}
							value={search}
							onChange={(event) => {
								setSearch(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<Select
							label={t("users.deletionFilter")}
							data={[
								{ value: "all", label: t("users.deletionAll") },
								{ value: "requested", label: t("users.deletionOnly") },
							]}
							value={deletionOnly ? "requested" : "all"}
							onChange={(value) => {
								setDeletionOnly(value === "requested");
								setPage(1);
							}}
							allowDeselect={false}
						/>
						<Select
							label={t("common.rows")}
							data={[
								{ value: "20", label: "20" },
								{ value: "50", label: "50" },
								{ value: "100", label: "100" },
							]}
							value={String(limit)}
							onChange={(value) => {
								if (value) {
									setLimit(Number(value) as PageLimit);
									setPage(1);
								}
							}}
						/>
					</Group>

					{listQuery.isLoading ? (
						<Group justify="center" py="xl">
							<Loader />
						</Group>
					) : null}

					{listQuery.error ? (
						<Text c="red">{describeApiErrorSync(listQuery.error)}</Text>
					) : null}

					{listQuery.data?.data.length === 0 ? (
						<Text c="dimmed" ta="center" py="xl">
							{t("users.empty")}
						</Text>
					) : null}

					{listQuery.data && listQuery.data.data.length > 0 ? (
						<Table.ScrollContainer type="native" minWidth={720}>
							<Table verticalSpacing="sm" highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<SortHeader
											label={t("users.sortName")}
											order={order}
											active={sort === "name"}
											onSort={() => onSort("name")}
										/>
										<Table.Th>{t("common.phone")}</Table.Th>
										<SortHeader
											label={t("users.registered")}
											order={order}
											active={sort === "createdAt"}
											onSort={() => onSort("createdAt")}
										/>
										<SortHeader
											label={t("users.deletionRequested")}
											order={order}
											active={sort === "deletionRequested"}
											onSort={() => onSort("deletionRequested")}
										/>
										<Table.Th>{t("users.discounts")}</Table.Th>
										<Table.Th />
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{listQuery.data.data.map((row) => (
										<Table.Tr key={row.id}>
											<Table.Td>
												<Group gap={6}>
													<UnstyledButton
														onClick={() => navigate(`/users/${row.id}`)}
													>
														<Anchor component="span">
															{[row.name, row.surname]
																.filter(Boolean)
																.join(" ") || t("common.dash")}
														</Anchor>
													</UnstyledButton>
													{row.deletionRequestedAt ? (
														<Badge color="red" variant="filled" size="sm">
															{t("users.deletionRequested")}
														</Badge>
													) : null}
												</Group>
											</Table.Td>
											<Table.Td>{row.phone}</Table.Td>
											<Table.Td>
												{new Date(row.createdAt).toLocaleString("uk-UA")}
											</Table.Td>
											<Table.Td>
												{row.deletionRequestedAt
													? new Date(row.deletionRequestedAt).toLocaleString(
															"uk-UA",
														)
													: t("common.dash")}
											</Table.Td>
											<Table.Td>
												<Group gap={4}>
													{row.discounts.length === 0 ? (
														<Text size="sm" c="dimmed">
															{t("common.dash")}
														</Text>
													) : (
														row.discounts.map((d) => (
															<Badge
																key={`${d.type}-${d.id ?? "corp"}`}
																variant="light"
																size="sm"
																color={d.isActive ? "playBlue" : "gray"}
															>
																{d.title}
																{d.isActive ? "" : ` (${t("common.inactive")})`}
															</Badge>
														))
													)}
												</Group>
											</Table.Td>
											<Table.Td>
												<Button
													size="xs"
													color="red"
													variant="light"
													disabled={deleteMutation.isPending}
													onClick={(event) => {
														event.stopPropagation();
														setDeleteError(null);
														setPendingDelete(row);
													}}
												>
													{t("users.delete")}
												</Button>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					) : null}

					<Group justify="space-between">
						<Text size="sm" c="dimmed">
							{t("common.page")} {page}
							{updatedAt
								? ` · ${t("users.updatedAt", { time: updatedAt })}`
								: ""}
						</Text>
						<Group>
							<Button
								variant="default"
								disabled={page <= 1 || listQuery.isFetching}
								onClick={() => setPage((p) => Math.max(1, p - 1))}
							>
								{t("common.previous")}
							</Button>
							<Button
								variant="default"
								disabled={!listQuery.data?.hasNextPage || listQuery.isFetching}
								onClick={() => setPage((p) => p + 1)}
							>
								{t("common.next")}
							</Button>
						</Group>
					</Group>
				</Stack>
			</Paper>
			<ConfirmDialog
				opened={pendingDelete !== null}
				title={t("users.deleteTitle")}
				body={
					deleteError
						? `${t("users.deleteConfirm", { phone: pendingDelete?.phone ?? "" })} ${deleteError}`
						: t("users.deleteConfirm", { phone: pendingDelete?.phone ?? "" })
				}
				confirmLabel={t("users.delete")}
				loading={deleteMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (pendingDelete) {
						deleteMutation.mutate(pendingDelete.id);
					}
				}}
			/>
		</Stack>
	);
}
